/**
 * Inline-resolution form rendered inside a ReadinessFindingCard for
 * pre_mapping findings. One form variant per ResolutionKind:
 *
 *   set_constant        — text field
 *   pick_source         — radio-list of candidate (source_table, source_column)
 *   add_transformation  — multi-line text field (cleanup expression)
 *   register_lookup     — file upload + alias
 *   skip_column         — button only ("Leave empty")
 *
 * Submission is reported back via ``onSubmit`` so the host (the panel)
 * can call ``POST /api/v1/mapping/readiness/resolve`` and refresh state.
 * The form is stateful only for in-progress edits; persisted state
 * lives upstream.
 *
 * Plain-English copy throughout — no platform jargon, no internal
 * codes, per CLAUDE.md #6.
 */
import React, { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

import type {
  ReadinessFinding,
  ReadinessResolutionRequest,
  ResolutionKind,
  ResolutionOption,
} from '../../../types/readiness';

interface ReadinessResolutionFormProps {
  finding: ReadinessFinding;
  onSubmit: (request: ReadinessResolutionRequest) => Promise<void>;
  /**
   * Upload a lookup file to the run's session. Returns the canonical
   * source alias the server registered the file under, which the form
   * then includes in the ``register_lookup`` submission payload.
   */
  onUploadLookup?: (file: File) => Promise<{ alias: string }>;
  /** Set externally while ``onSubmit`` is in flight. */
  submitting?: boolean;
  /** Set externally when the resolve endpoint reports a validation error. */
  errorMessage?: string;
}

// Short labels for the radio list. Each is a clean noun phrase that
// reads naturally after "How would you like to resolve this?".
const KIND_HEADING: Record<ResolutionKind, string> = {
  set_constant: 'Use a fixed value',
  pick_source: 'Pick a source column',
  add_transformation: 'Add a cleanup rule',
  register_lookup: 'Upload a lookup file',
  skip_column: 'Leave the field empty',
  generate_uuid: 'Auto-generate a unique ID',
  generate_sequence: 'Auto-number rows',
  comment: 'Add a comment for the agent',
};

// One-line description shown under each radio so the operator
// understands what the option does without reading docs.
const KIND_HELP: Record<ResolutionKind, string> = {
  set_constant: 'Apply the same value to every row.',
  pick_source: 'Map this field from a specific source column.',
  add_transformation: 'Write an expression to clean up or convert values.',
  register_lookup: 'Add a reference file the mapping can join against.',
  skip_column: 'Leave this field blank for every row.',
  generate_uuid: 'Generate a unique ID per row automatically.',
  generate_sequence: 'Number rows 1, 2, 3 … in source order.',
  comment: "Describe how this should be handled in plain English.",
};

function targetParts(finding: ReadinessFinding): {
  table: string;
  column: string;
} {
  const path = finding.target_path || '';
  const [pathTable, pathColumn] = path.split('.', 2);
  // For findings whose target_path is element-only (no dot — e.g.
  // PLAN_CONTRACT_VIOLATION targets a level), fall back to the
  // primary_action_target payload that emit sites already populate
  // with the real source-domain link column. Keeps the workspace_meta
  // key in canonical ``table.column`` form so the recipe overlay can
  // bind it to a concrete column at materialise time.
  const pat = (finding.primary_action_target || {}) as Record<string, unknown>;
  const fallbackTable = (pat.target_table as string) || pathTable || '';
  const fallbackColumn =
    (pat.target_field as string)
    || (pat.target_column as string)
    || (pat.parent_key as string)
    || pathColumn
    || '';
  return {
    table: pathTable || fallbackTable,
    column: pathColumn || fallbackColumn,
  };
}

// Resolution kinds that mutate the recipe and therefore need a
// concrete (table, column) pair — kept in lockstep with the
// ``_RECIPE_MUTATING`` set on the resolve endpoint. When the finding
// doesn't carry a column, these options would always fail server-
// side validation, so we filter them out of the radio list rather
// than letting the operator pick an action that can't apply.
const RECIPE_MUTATING_KINDS: ReadonlySet<ResolutionKind> = new Set([
  'set_constant',
  'skip_column',
  'pick_source',
  'generate_uuid',
  'generate_sequence',
  'add_transformation',
] as ResolutionKind[]);

export default function ReadinessResolutionForm({
  finding,
  onSubmit,
  onUploadLookup,
  submitting = false,
  errorMessage,
}: ReadinessResolutionFormProps) {
  const allOptions: ResolutionOption[] = finding.resolution_options ?? [];
  const { table, column } = targetParts(finding);
  // Hide options that can't apply: when the finding has no concrete
  // target column, recipe-mutating resolutions would be rejected by
  // the resolve endpoint with "we can't apply this deterministically".
  // The operator still has ``comment`` / ``register_lookup`` to act on
  // the finding.
  const visibleOptions: ResolutionOption[] = column
    ? allOptions
    : allOptions.filter((o) => !RECIPE_MUTATING_KINDS.has(o.kind));

  // Collapse duplicate-kind options the agent may have emitted (one
  // ``pick_source`` per candidate, etc.) into a single canonical option
  // per kind. Without this, the RadioGroup below would render multiple
  // <Radio value={kind}> entries sharing the same value — MUI would
  // mark every entry with that value as "checked" simultaneously,
  // producing the "multiple options selected" bug seen in the UI.
  //
  // Merge rules per kind:
  //   * ``pick_source``: candidates lists are concatenated and de-
  //     duplicated by (source_table, source_column). Label falls back
  //     to the canonical KIND_HEADING since per-candidate labels
  //     already render inside ``PickSourceForm``.
  //   * Any other kind: the first occurrence wins. Later duplicates
  //     are dropped.
  const options: ResolutionOption[] = React.useMemo(() => {
    const seen = new Map<ResolutionKind, ResolutionOption>();
    const order: ResolutionKind[] = [];
    for (const opt of visibleOptions) {
      const prev = seen.get(opt.kind);
      if (!prev) {
        // First-seen copy with cloned payload_schema so we can merge
        // future entries without mutating the source ResolutionOption.
        seen.set(opt.kind, {
          ...opt,
          payload_schema: { ...(opt.payload_schema || {}) },
        });
        order.push(opt.kind);
        continue;
      }
      if (opt.kind === 'pick_source') {
        const prevCands =
          (prev.payload_schema?.candidates as SourceCandidatePayload[] | undefined) ?? [];
        const nextCands =
          (opt.payload_schema?.candidates as SourceCandidatePayload[] | undefined) ?? [];
        const seenKey = new Set(
          prevCands.map((c) => `${c.source_table}.${c.source_column}`),
        );
        for (const c of nextCands) {
          const key = `${c.source_table}.${c.source_column}`;
          if (!seenKey.has(key)) {
            prevCands.push(c);
            seenKey.add(key);
          }
        }
        prev.payload_schema = {
          ...(prev.payload_schema || {}),
          candidates: prevCands,
        };
        // Use the canonical heading once we've merged multiple — the
        // per-candidate label only makes sense inside PickSourceForm,
        // not as the top-level radio label.
        prev.label = KIND_HEADING.pick_source;
      }
    }
    return order.map((kind) => seen.get(kind)!).filter(Boolean);
  }, [visibleOptions]);

  const [activeKind, setActiveKind] = useState<ResolutionKind | null>(
    options[0]?.kind ?? null,
  );

  // Keep the active selection in sync with the visible options. If the
  // filter list changes (e.g. the finding's column is resolved
  // upstream, unhiding recipe-mutating kinds) and the previously-
  // selected kind disappears, fall back to the first available kind —
  // otherwise the per-form selector below ends up with ``undefined``
  // and the children crash on ``option.payload_schema``.
  React.useEffect(() => {
    if (!options.length) {
      if (activeKind !== null) setActiveKind(null);
      return;
    }
    const stillThere = options.some((o) => o.kind === activeKind);
    if (!stillThere) {
      setActiveKind(options[0].kind);
    }
  }, [options, activeKind]);

  if (!options.length || !finding.finding_id) {
    return null;
  }
  // Pick the option backing the currently-active form. Filtered-out
  // kinds (e.g. the operator picks ``set_constant`` then the column
  // disappears) leave ``activeKind`` stale for one render — guard the
  // children so they don't blow up trying to read payload_schema on
  // ``undefined``.
  const activeOption: ResolutionOption | undefined =
    options.find((o) => o.kind === activeKind);
  const handleSubmit = async (payload: Record<string, unknown>) => {
    if (!activeKind) return;
    await onSubmit({
      finding_id: finding.finding_id!,
      kind: activeKind,
      payload,
      target_table: table,
      target_column: column,
    });
  };

  return (
    <Box sx={{ mt: 1, pt: 1.5, borderTop: '1px dashed', borderColor: 'divider' }}>
      {options.length > 1 && (
        <FormControl component="fieldset" sx={{ width: '100%', mb: 1.5 }}>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: 'block', mb: 0.5, fontWeight: 600 }}
          >
            How would you like to resolve this?
          </Typography>
          <RadioGroup
            value={activeKind || ''}
            onChange={(_, next) => setActiveKind(next as ResolutionKind)}
          >
            {options.map((opt) => (
              // ``key`` and ``value`` are now guaranteed unique per kind
              // because the options list is deduped above. Without that
              // guarantee, duplicate values caused every radio sharing
              // the active value to render as checked simultaneously
              // (the "multiple options selected" UI bug).
              <FormControlLabel
                key={opt.kind}
                value={opt.kind}
                control={<Radio size="small" sx={{ py: 0.25 }} />}
                sx={{
                  alignItems: 'flex-start',
                  ml: 0,
                  mb: 0.5,
                  '& .MuiFormControlLabel-label': { lineHeight: 1.3 },
                }}
                label={
                  <Box sx={{ pt: 0.25 }}>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {opt.label || KIND_HEADING[opt.kind]}
                    </Typography>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ display: 'block', lineHeight: 1.35 }}
                    >
                      {KIND_HELP[opt.kind]}
                    </Typography>
                  </Box>
                }
              />
            ))}
          </RadioGroup>
        </FormControl>
      )}

      {options.length === 1 && (
        <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
          {options[0].label || KIND_HEADING[options[0].kind]}
        </Typography>
      )}

      {errorMessage && (
        <Alert severity="error" sx={{ mb: 1 }}>
          {errorMessage}
        </Alert>
      )}

      {activeKind === 'set_constant' && activeOption && (
        <SetConstantForm
          option={activeOption}
          submitting={submitting}
          onSubmit={handleSubmit}
        />
      )}
      {activeKind === 'pick_source' && activeOption && (
        <PickSourceForm
          option={activeOption}
          submitting={submitting}
          onSubmit={handleSubmit}
        />
      )}
      {activeKind === 'add_transformation' && activeOption && (
        <AddTransformationForm
          option={activeOption}
          submitting={submitting}
          onSubmit={handleSubmit}
        />
      )}
      {activeKind === 'register_lookup' && activeOption && (
        <RegisterLookupForm
          option={activeOption}
          submitting={submitting}
          onSubmit={handleSubmit}
          onUpload={onUploadLookup}
        />
      )}
      {activeKind === 'skip_column' && (
        <SkipColumnForm submitting={submitting} onSubmit={handleSubmit} />
      )}
      {activeKind === 'generate_uuid' && (
        <AutoGenerateForm
          submitting={submitting}
          onSubmit={handleSubmit}
          helpText="Each row will get its own unique ID. Useful when your source data has no natural identifier."
          confirmLabel="Save"
        />
      )}
      {activeKind === 'generate_sequence' && (
        <AutoGenerateForm
          submitting={submitting}
          onSubmit={handleSubmit}
          helpText="Rows will be numbered 1, 2, 3, … in source order. Common for repeat-key columns."
          confirmLabel="Save"
        />
      )}
      {activeKind === 'comment' && (
        <CommentForm submitting={submitting} onSubmit={handleSubmit} />
      )}
    </Box>
  );
}

function CommentForm({
  submitting,
  onSubmit,
}: {
  submitting: boolean;
  onSubmit: (payload: Record<string, unknown>) => Promise<void>;
}) {
  const [text, setText] = useState('');
  const MAX = 4000;
  const remaining = MAX - text.length;
  return (
    <Stack spacing={1}>
      <Typography variant="caption" color="text.secondary">
        If none of the other options fit, describe how this should be
        handled. Your note guides the agent on the next mapping pass.
      </Typography>
      <TextField
        multiline
        minRows={2}
        size="small"
        value={text}
        onChange={(e) => setText(e.target.value.slice(0, MAX))}
        placeholder="Why none of the options above fit, and what to do instead."
        disabled={submitting}
        fullWidth
        helperText={`${remaining} characters left`}
      />
      <Box>
        <Button
          variant="contained"
          size="small"
          disabled={submitting || !text.trim()}
          onClick={() => onSubmit({ text })}
          startIcon={submitting ? <CircularProgress size={14} /> : null}
        >
          Save
        </Button>
      </Box>
    </Stack>
  );
}

function AutoGenerateForm({
  submitting,
  onSubmit,
  helpText,
  confirmLabel,
}: {
  submitting: boolean;
  onSubmit: (payload: Record<string, unknown>) => Promise<void>;
  helpText: string;
  confirmLabel: string;
}) {
  return (
    <Stack direction="row" spacing={1} alignItems="center">
      <Typography variant="body2" color="text.secondary" sx={{ flex: 1 }}>
        {helpText}
      </Typography>
      <Button
        variant="contained"
        size="small"
        disabled={submitting}
        onClick={() => onSubmit({})}
        startIcon={submitting ? <CircularProgress size={14} /> : null}
      >
        {confirmLabel}
      </Button>
    </Stack>
  );
}

// ─── Per-kind forms ──────────────────────────────────────────────────

/**
 * Map raw target column data-types to plain-English noun phrases. Raw
 * SQL / XSD type names (VARCHAR, INTEGER, xs:date, etc.) are jargon
 * per CLAUDE.md #6 and must not surface in user copy.
 */
function plainValueLabel(rawType: string | undefined): string {
  const t = (rawType || '').toLowerCase();
  if (
    t === 'string' ||
    t === 'text' ||
    t === 'varchar' ||
    t === 'char' ||
    t.startsWith('varchar') ||
    t.startsWith('char(')
  ) {
    return 'text value';
  }
  if (
    t === 'integer' ||
    t === 'int' ||
    t === 'bigint' ||
    t === 'smallint' ||
    t === 'long'
  ) {
    return 'whole number';
  }
  if (
    t === 'float' ||
    t === 'double' ||
    t === 'decimal' ||
    t === 'numeric' ||
    t === 'number' ||
    t.startsWith('decimal(') ||
    t.startsWith('numeric(')
  ) {
    return 'number';
  }
  if (t === 'boolean' || t === 'bool') {
    return 'yes/no value';
  }
  if (t === 'date') return 'date (YYYY-MM-DD)';
  if (t === 'datetime' || t === 'timestamp') return 'date and time';
  return 'value';
}

function SetConstantForm({
  option,
  submitting,
  onSubmit,
}: {
  option: ResolutionOption;
  submitting: boolean;
  onSubmit: (payload: Record<string, unknown>) => Promise<void>;
}) {
  const [value, setValue] = useState('');
  const valueLabel = plainValueLabel(
    option.payload_schema?.value_type as string | undefined,
  );
  return (
    <Stack direction="row" spacing={1} alignItems="center">
      <TextField
        size="small"
        value={value}
        onChange={(e) => setValue(e.target.value)}
        placeholder={`Type a ${valueLabel}`}
        disabled={submitting}
        fullWidth
      />
      <Button
        variant="contained"
        size="small"
        disabled={submitting || !value.trim()}
        onClick={() => onSubmit({ value })}
        startIcon={submitting ? <CircularProgress size={14} /> : null}
      >
        Save
      </Button>
    </Stack>
  );
}

interface SourceCandidate {
  source_table: string;
  source_column: string;
}

// Shared alias used by the option-deduper above; identical shape to
// the form-local SourceCandidate but referenced before that type's
// declaration. Re-exporting under a second name keeps the dedup logic
// near the top of the file readable without forward-reference clutter.
type SourceCandidatePayload = SourceCandidate;

function PickSourceForm({
  option,
  submitting,
  onSubmit,
}: {
  option: ResolutionOption;
  submitting: boolean;
  onSubmit: (payload: Record<string, unknown>) => Promise<void>;
}) {
  const candidates: SourceCandidate[] =
    (option.payload_schema?.candidates as SourceCandidate[] | undefined) ?? [];
  const [picked, setPicked] = useState<string>('');

  if (!candidates.length) {
    return (
      <Typography variant="body2" color="text.secondary">
        No source columns are loaded yet. Upload one of your data files first.
      </Typography>
    );
  }

  return (
    <Stack spacing={1}>
      <FormControl>
        <RadioGroup
          value={picked}
          onChange={(e) => setPicked(e.target.value)}
        >
          {candidates.map((c, i) => {
            const key = `${c.source_table}.${c.source_column}`;
            return (
              <FormControlLabel
                key={key + i}
                value={key}
                control={<Radio size="small" />}
                label={
                  <Typography variant="body2">
                    <strong>{c.source_column}</strong>
                    <Box
                      component="span"
                      sx={{ color: 'text.secondary', ml: 1 }}
                    >
                      from {c.source_table}
                    </Box>
                  </Typography>
                }
              />
            );
          })}
        </RadioGroup>
      </FormControl>
      <Box>
        <Button
          variant="contained"
          size="small"
          disabled={submitting || !picked}
          onClick={() => {
            const [source_table, source_column] = picked.split('.', 2);
            onSubmit({ source_table, source_column });
          }}
          startIcon={submitting ? <CircularProgress size={14} /> : null}
        >
          Save
        </Button>
      </Box>
    </Stack>
  );
}

function AddTransformationForm({
  option,
  submitting,
  onSubmit,
}: {
  option: ResolutionOption;
  submitting: boolean;
  onSubmit: (payload: Record<string, unknown>) => Promise<void>;
}) {
  const suggested =
    (option.payload_schema?.suggested_expression as string | undefined) || '';
  const [expr, setExpr] = useState(suggested);
  return (
    <Stack spacing={1}>
      <Typography variant="caption" color="text.secondary">
        {suggested
          ? 'A starting suggestion is filled in below — edit it to fit your data.'
          : 'Write a cleanup expression — e.g. UPPER(column), COALESCE(value, "UNKNOWN").'}
      </Typography>
      <TextField
        multiline
        minRows={2}
        size="small"
        value={expr}
        onChange={(e) => setExpr(e.target.value)}
        placeholder="UPPER(column_name)"
        disabled={submitting}
        fullWidth
      />
      <Box>
        <Button
          variant="contained"
          size="small"
          disabled={submitting || !expr.trim()}
          onClick={() => onSubmit({ expression: expr })}
          startIcon={submitting ? <CircularProgress size={14} /> : null}
        >
          Save
        </Button>
      </Box>
    </Stack>
  );
}

function RegisterLookupForm({
  option,
  submitting,
  onSubmit,
  onUpload,
}: {
  option: ResolutionOption;
  submitting: boolean;
  onSubmit: (payload: Record<string, unknown>) => Promise<void>;
  onUpload?: (file: File) => Promise<{ alias: string }>;
}) {
  const expected =
    (option.payload_schema?.expected_table_name as string | undefined) ?? '';
  const [uploading, setUploading] = useState(false);
  const [uploaded, setUploaded] = useState<{ alias: string } | null>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const fileInputRef = React.useRef<HTMLInputElement | null>(null);

  const handleFile = async (file: File) => {
    if (!onUpload) {
      setUploadError('File upload isn’t configured in this view.');
      return;
    }
    setUploadError(null);
    setUploading(true);
    try {
      const result = await onUpload(file);
      setUploaded(result);
    } catch (e: any) {
      setUploadError(
        e?.response?.data?.detail ||
          e?.message ||
          'We couldn’t upload that file.',
      );
    } finally {
      setUploading(false);
    }
  };

  return (
    <Stack spacing={1}>
      <Typography variant="caption" color="text.secondary">
        {expected
          ? `Pick a ${expected} file with the matching values.`
          : 'Pick a reference file with the matching values.'}
      </Typography>
      <input
        ref={fileInputRef}
        type="file"
        accept=".csv,.tsv,.xlsx,.xls,.parquet"
        style={{ display: 'none' }}
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) void handleFile(file);
          e.target.value = '';
        }}
      />
      {uploadError && <Alert severity="error">{uploadError}</Alert>}
      {uploaded ? (
        <Typography variant="body2">
          Uploaded as <strong>{uploaded.alias}</strong>.
        </Typography>
      ) : (
        <Box>
          <Button
            variant="outlined"
            size="small"
            disabled={uploading || submitting}
            startIcon={uploading ? <CircularProgress size={14} /> : null}
            onClick={() => fileInputRef.current?.click()}
          >
            {uploading ? 'Uploading…' : 'Choose file'}
          </Button>
        </Box>
      )}
      <Box>
        <Button
          variant="contained"
          size="small"
          disabled={submitting || !uploaded}
          onClick={() =>
            onSubmit({
              source_alias: uploaded?.alias,
              expected_table_name: expected,
            })
          }
          startIcon={submitting ? <CircularProgress size={14} /> : null}
        >
          Save
        </Button>
      </Box>
    </Stack>
  );
}

function SkipColumnForm({
  submitting,
  onSubmit,
}: {
  submitting: boolean;
  onSubmit: (payload: Record<string, unknown>) => Promise<void>;
}) {
  return (
    <Stack direction="row" spacing={1} alignItems="center">
      <Typography variant="body2" color="text.secondary">
        This field will be left empty for every row.
      </Typography>
      <Button
        variant="outlined"
        size="small"
        disabled={submitting}
        onClick={() => onSubmit({})}
        startIcon={submitting ? <CircularProgress size={14} /> : null}
      >
        Save
      </Button>
    </Stack>
  );
}
